/**
 * 세무관리 — 원천징수이행상황신고 리포트 + EDI 다운로드
 * 홈택스 자동 업로드는 하지 않는다. 사람이 내려받은 파일을 홈택스에 올린다.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer, CommonCss } from 'styles/common.styles';
import { SEARCH_LABEL_W, theme, cssVar } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import { showAlert } from 'utils/toastBridge';
import { clientOptions, loadClients } from 'tax/clientCompanyStore';
import {
  MONTH_OPTIONS,
  payYm,
  loadAggregate,
  loadLines,
  aggregatePayroll,
  downloadEdiFile,
  formatAmt,
} from 'tax/whtPayrollStore';

const GRID_ID = 'tx-wht-report-lines';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const ReportPaper = styled.div`
  flex: 0 0 auto;
  border: 1px solid ${cssVar('color-border', theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  background: ${cssVar('color-bg-white', theme.colors.white)};
  padding: 16px 20px;
  color: ${cssVar('color-text', theme.colors.text.primary)};
`;

const ReportTitle = styled.h2`
  margin: 0 0 12px;
  text-align: center;
  font-size: ${theme.typography.fontSize.lg};
  font-weight: 700;
`;

const ReportMeta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 24px;
  font-size: ${theme.typography.fontSize.sm};
  margin-bottom: 12px;
`;

const ReportTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.fontSize.sm};

  th,
  td {
    border: 1px solid ${cssVar('color-border', theme.colors.border)};
    padding: 6px 8px;
  }
  th {
    background: ${cssVar('color-bg-light', theme.colors.background.light)};
    font-weight: 600;
    text-align: center;
  }
  td.num {
    text-align: right;
    font-variant-numeric: tabular-nums;
  }
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${cssVar('color-text-secondary', theme.colors.text.secondary)};
  flex-shrink: 0;
`;

const GridWrap = styled.div`
  flex: 1;
  min-height: 0;
`;

function currentYear() {
  return String(new Date().getFullYear());
}

function ymLabel(ym) {
  const s = String(ym || '');
  if (s.length !== 6) return s || '—';
  return `${s.slice(0, 4)}-${s.slice(4)}`;
}

function WhtReportInner() {
  const gridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const [snapshot, setSnapshot] = useState(null);
  const [clientTick, setClientTick] = useState(0);

  const clientOpts = useMemo(() => {
    loadClients(tenantCode);
    return clientOptions(tenantCode, { includeAll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantCode, clientTick]);

  const searchDefaults = useMemo(
    () => ({
      tenantCode,
      clientCode: clientOpts.find((o) => o.value)?.value || '',
      fiscalYear: currentYear(),
      month: '08',
    }),
    [tenantCode, clientOpts]
  );

  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  const columns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '사번', field: 'empNo', size: 90 }),
      hiCol.roTextColumn({ header: '성명', field: 'empName', size: 100 }),
      hiCol.roTextColumn({ header: '주민등록번호', field: 'rrn', size: 130 }),
      hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
      hiCol.roNumberColumn({ header: '과세소득', field: 'taxableIncome', size: 110 }),
      hiCol.roNumberColumn({ header: '비과세', field: 'nontaxable', size: 100 }),
      hiCol.roNumberColumn({ header: '부양가족', field: 'dependents', size: 90, align: 'center' }),
      hiCol.roNumberColumn({ header: '소득세', field: 'incomeTax', size: 100 }),
      hiCol.roNumberColumn({ header: '지방소득세', field: 'localTax', size: 110 }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    customOnSearch: async () => gridRef.current?.getData?.() ?? [],
  });

  const handleSearch = useCallback(async () => {
    setClientTick((n) => n + 1);
    const clientCode = String(getValues('clientCode') || '').trim();
    const ym = payYm(getValues('fiscalYear'), getValues('month'));
    if (!clientCode) {
      showAlert('고객사를 선택하세요.', 'warning');
      return;
    }
    try {
      let snap = loadAggregate(tenantCode, clientCode, ym);
      let lines = loadLines(tenantCode, clientCode, ym);
      if (!snap && lines.length) {
        const result = aggregatePayroll(tenantCode, clientCode, ym, lines);
        snap = result.snapshot;
        lines = result.lines;
      }
      setSnapshot(snap);
      gridRef.current?.setData?.(lines || []);
      if (!snap) {
        showAlert('집계 자료가 없습니다. 급여대장 업로드 화면에서 집계를 실행하세요.', 'warning');
      }
    } catch (e) {
      showAlert(e?.message || '조회 실패', 'error');
    }
  }, [getValues, tenantCode]);

  useEffect(() => {
    const clientCode = String(getValues('clientCode') || '').trim();
    if (!clientCode) return;
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantCode]);

  const handleEdi = useCallback(() => {
    const lines = gridRef.current?.getData?.() || [];
    if (!snapshot) {
      showAlert('조회된 신고 자료가 없습니다.', 'warning');
      return;
    }
    downloadEdiFile({ snapshot, lines });
    showAlert('전산매체 파일을 내려받았습니다. 홈택스 업로드는 직접 수행하세요.', 'success');
  }, [snapshot]);

  const handlePrint = useCallback(() => {
    if (!snapshot) {
      showAlert('조회된 신고 자료가 없습니다.', 'warning');
      return;
    }
    const w = window.open('', '_blank');
    if (!w) {
      showAlert('팝업이 차단되었습니다.', 'warning');
      return;
    }
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>원천징수이행상황신고서</title>
<style>
body{font-family:sans-serif;padding:24px;color:#111}
h1{text-align:center;font-size:18px}
table{width:100%;border-collapse:collapse;margin-top:12px}
th,td{border:1px solid #333;padding:6px 8px;font-size:13px}
th{background:#eee}
.num{text-align:right}
.meta{margin:8px 0;font-size:13px}
</style></head><body>
<h1>원천징수이행상황신고서</h1>
<div class="meta">신고의무자: ${snapshot.clientName || ''} &nbsp; 사업자번호: ${snapshot.bizNo || ''}</div>
<div class="meta">대표자: ${snapshot.ceoName || ''} &nbsp; 귀속연월: ${ymLabel(snapshot.payYm)}</div>
<div class="meta">소재지: ${snapshot.address || ''} &nbsp; 관할세무서: ${snapshot.taxOffice || ''}</div>
<table>
<thead><tr><th>소득종류</th><th>인원</th><th>총지급액</th><th>소득세</th><th>지방소득세</th><th>계</th></tr></thead>
<tbody><tr>
<td>${snapshot.incomeKind || '근로소득(간이세액)'}</td>
<td class="num">${formatAmt(snapshot.count)}</td>
<td class="num">${formatAmt(snapshot.totalPay)}</td>
<td class="num">${formatAmt(snapshot.totalIncomeTax)}</td>
<td class="num">${formatAmt(snapshot.totalLocalTax)}</td>
<td class="num">${formatAmt(snapshot.totalTax)}</td>
</tr></tbody>
</table>
<p style="margin-top:16px;font-size:12px">홈택스 서식 레이아웃 참고 미리보기입니다. 전산매체 파일은 EDI다운로드로 생성합니다.</p>
</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  }, [snapshot]);

  const getData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getDataColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);
  useRegisterGridExport({
    id: GRID_ID,
    label: '원천징수라인',
    getData,
    getDataColumns,
    order: 0,
  });
  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '원천징수이행상황신고' });

  const actionButtons = [
    {
      id: 'search',
      label: '조회',
      icon: '🔍',
      onClick: handleSearch,
      hotkey: 'F7',
    },
    excelButton,
  ];

  const extraButtons = [
    { id: 'print', label: '미리보기', icon: '🖨', onClick: handlePrint, title: '신고서 인쇄 미리보기' },
    { id: 'edi', label: 'EDI다운로드', icon: '📥', onClick: handleEdi, title: '전산매체 파일 생성 (홈택스 수동 업로드)' },
  ];

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'clientCode',
          label: '고객사',
          type: 'select',
          width: CommonCss.w200,
          labelWidth: SEARCH_LABEL_W,
          options: clientOpts,
        },
        {
          name: 'fiscalYear',
          label: '귀속연도',
          type: 'fiscalYear',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'month',
          label: '월',
          type: 'select',
          width: CommonCss.w90,
          labelWidth: SEARCH_LABEL_W,
          options: MONTH_OPTIONS,
        },
      ],
    ],
    [clientOpts]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} loading={grid.loading} />
        <CollapsibleSearchPanel storageKey="tx-wht-report" prefsUserId={prefsUserId} title="조회조건">
          <FormArea compact fields={searchFields} onFieldEnter={handleSearch} />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Hint>
        홈택스 자동 업로드는 하지 않습니다. 미리보기·EDI 파일 생성까지가 범위입니다.
      </Hint>

      <Body>
        <ReportPaper>
          <ReportTitle>원천징수이행상황신고서</ReportTitle>
          <ReportMeta>
            <div>신고의무자 {snapshot?.clientName || '—'}</div>
            <div>사업자번호 {snapshot?.bizNo || '—'}</div>
            <div>대표자 {snapshot?.ceoName || '—'}</div>
            <div>귀속연월 {snapshot ? ymLabel(snapshot.payYm) : '—'}</div>
            <div>소재지 {snapshot?.address || '—'}</div>
            <div>관할세무서 {snapshot?.taxOffice || '—'}</div>
          </ReportMeta>
          <ReportTable>
            <thead>
              <tr>
                <th>소득종류</th>
                <th>인원</th>
                <th>총지급액</th>
                <th>소득세</th>
                <th>지방소득세</th>
                <th>계</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{snapshot?.incomeKind || '근로소득(간이세액)'}</td>
                <td className="num">{snapshot ? formatAmt(snapshot.count) : '—'}</td>
                <td className="num">{snapshot ? formatAmt(snapshot.totalPay) : '—'}</td>
                <td className="num">{snapshot ? formatAmt(snapshot.totalIncomeTax) : '—'}</td>
                <td className="num">{snapshot ? formatAmt(snapshot.totalLocalTax) : '—'}</td>
                <td className="num">{snapshot ? formatAmt(snapshot.totalTax) : '—'}</td>
              </tr>
            </tbody>
          </ReportTable>
        </ReportPaper>

        <GridWrap>
          <GridExportFocusScope gridId={GRID_ID} style={{ height: '100%', minHeight: 0 }}>
            <HiGridComponent
              ref={gridRef}
              columns={columns}
              height="100%"
              showRowNumber
              rowNumberSize={56}
              enableColumnFilters
              enableContextMenu
              enableRowEditMenu={false}
              enableCut={false}
              allowReplace={false}
              enableFindReplace
              enableHeaderMenu
              enableSelectionStats
              enableFreeze
              enableQuickExport
              showModColumn={false}
              exportFileName="원천징수라인"
              gridId={GRID_ID}
              prefsUserId={prefsUserId}
              {...grid.contextMenuProps}
            />
          </GridExportFocusScope>
        </GridWrap>
      </Body>
      {excelDialog}
    </PageContainer>
  );
}

const WhtReport = () => (
  <GridExportProvider>
    <WhtReportInner />
  </GridExportProvider>
);

export default WhtReport;
